import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import React, { FunctionComponent } from 'react';
import styles from './ChannelCard.module.scss';

export type ChannelStatus = 'connected' | 'active' | 'pending' | 'notConnected' | 'unavailable' | 'comingSoon';

interface ChannelCardProps {
    icon: string;
    title: string;
    description: React.ReactNode;
    status: ChannelStatus;
    /** Channels the business configures. Omit for platform-provided ones: no action, no button. */
    buttonLabel?: string;
    buttonDisabled?: boolean;
    buttonLoading?: boolean;
    /** Quiet line under the card when there is nothing to configure. */
    footnote?: string;
    /** Dimmed icon + dashed edge: the channel exists in the product but is not running here. */
    muted?: boolean;
    onClick?: () => void;
}

const BADGE_LABELS: Record<ChannelStatus, string> = {
    connected: 'Connected',
    active: 'Active',
    pending: 'Pending Verification',
    notConnected: 'Not Connected',
    unavailable: 'Not available',
    comingSoon: 'Coming Soon'
};

const BADGE_CLASSES: Record<ChannelStatus, string> = {
    connected: styles.badgeConnected,
    active: styles.badgePlain,
    pending: styles.badgePending,
    notConnected: styles.badgeDisconnected,
    unavailable: styles.badgeMuted,
    comingSoon: styles.badgeDisconnected
};

const ChannelCard: FunctionComponent<ChannelCardProps> = ({
    icon,
    title,
    description,
    status,
    buttonLabel,
    buttonDisabled,
    buttonLoading,
    footnote,
    muted,
    onClick
}) => {
    return (
        <div className={clsx(styles.card, muted && styles.cardMuted)}>
            <div className={styles.header}>
                <img
                    src={icon}
                    alt={title}
                    className={clsx(styles.icon, muted && styles.iconMuted)}
                />
                <h3 className={clsx(styles.title, muted && styles.titleMuted)}>{title}</h3>
                <span className={BADGE_CLASSES[status]}>{BADGE_LABELS[status]}</span>
            </div>

            {/* Everything under the title keeps the icon's indentation. */}
            <div className={styles.body}>
                <p className={styles.description}>{description}</p>

                {buttonLabel && (
                    <Button
                        label={buttonLabel}
                        variant="black"
                        size="medium"
                        className={styles.button}
                        disabled={buttonDisabled}
                        isLoading={buttonLoading}
                        onClick={onClick}
                    />
                )}

                {footnote && <p className={styles.footnote}>{footnote}</p>}
            </div>
        </div>
    );
};

export default ChannelCard;
